import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PaletteIcon from '@mui/icons-material/Palette';
import LayersIcon from '@mui/icons-material/Layers';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Stage, Layer, Image as KonvaImage, Text as KonvaText, Rect, Group, Transformer, Shape } from 'react-konva';
import { v4 as uuidv4 } from 'uuid';
import { collection, doc, onSnapshot, setDoc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import Konva from 'konva';
import DeleteIcon from '@mui/icons-material/Delete';

interface BoardElement {
  id: string;
  type: 'image' | 'text' | 'rect' | 'circle' | 'sticker';
  content: string; // image url or text
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  fontSize?: number;
  rotation?: number;
  locked?: boolean;
  layer?: number;
}

const BOARD_WIDTH = 1600;
const BOARD_HEIGHT = 1000;

const MoodBoardEditorV2: React.FC<{ boardId: string }> = ({ boardId }) => {
  const [elements, setElements] = useState<BoardElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [color, setColor] = useState<string>('#FFD93D');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [newText, setNewText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const [resizingId, setResizingId] = useState<string | null>(null);
  const [localTransforms, setLocalTransforms] = useState<Record<string, { width: number; height: number; rotation: number; x: number; y: number }>>({});

  // Real-time Firestore sync
  useEffect(() => {
    if (!boardId) return;
    const colRef = collection(firestore, `moodboards/${boardId}/elements`);
    const unsub = onSnapshot(colRef, snap => {
      setElements(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as BoardElement)));
    });
    return () => unsub();
  }, [boardId]);

  // Add image
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target?.result) {
          const el: Omit<BoardElement, 'id'> = {
            type: 'image',
            content: event.target.result as string,
            x: 100 + Math.random() * 200,
            y: 100 + Math.random() * 200,
            width: 300,
            height: 200,
          };
          const colRef = collection(firestore, `moodboards/${boardId}/elements`);
          await addDoc(colRef, el);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Add text
  const handleAddText = async () => {
    if (!newText.trim()) return;
    const el: Omit<BoardElement, 'id'> = {
      type: 'text',
      content: newText,
      x: 120 + Math.random() * 200,
      y: 120 + Math.random() * 200,
      width: 200,
      height: 60,
      color: color,
      fontSize: 28,
    };
    const colRef = collection(firestore, `moodboards/${boardId}/elements`);
    await addDoc(colRef, el);
    setShowTextDialog(false);
    setNewText('');
  };

  // Add shape functions
  const handleAddRect = async () => {
    const el: Omit<BoardElement, 'id'> = {
      type: 'rect',
      content: '',
      x: 150 + Math.random() * 200,
      y: 150 + Math.random() * 200,
      width: 120,
      height: 80,
      color: color,
    };
    const colRef = collection(firestore, `moodboards/${boardId}/elements`);
    await addDoc(colRef, el);
  };
  const handleAddCircle = async () => {
    const el: Omit<BoardElement, 'id'> = {
      type: 'circle',
      content: '',
      x: 200 + Math.random() * 200,
      y: 200 + Math.random() * 200,
      width: 80,
      height: 80,
      color: color,
    };
    const colRef = collection(firestore, `moodboards/${boardId}/elements`);
    await addDoc(colRef, el);
  };
  const handleAddSticker = async () => {
    const el: Omit<BoardElement, 'id'> = {
      type: 'sticker',
      content: '😊',
      x: 250 + Math.random() * 200,
      y: 250 + Math.random() * 200,
      width: 60,
      height: 60,
    };
    const colRef = collection(firestore, `moodboards/${boardId}/elements`);
    await addDoc(colRef, el);
  };

  // Drag, select, transform
  const handleSelect = (id: string) => setSelectedId(id);
  const handleDeselect = () => setSelectedId(null);
  const handleDrag = async (id: string, x: number, y: number) => {
    const elRef = doc(firestore, `moodboards/${boardId}/elements`, id);
    await updateDoc(elRef, { x, y });
  };
  const handleTransform = async (id: string, node: any) => {
    const elRef = doc(firestore, `moodboards/${boardId}/elements`, id);
    await updateDoc(elRef, {
      x: node.x(),
      y: node.y(),
      width: node.width() * node.scaleX(),
      height: node.height() * node.scaleY(),
      rotation: node.rotation(),
    });
    node.scaleX(1);
    node.scaleY(1);
  };

  // Layer management functions
  const bringForward = async () => {
    if (!selectedId) return;
    const el = elements.find(e => e.id === selectedId);
    if (!el) return;
    const maxLayer = Math.max(...elements.map(e => e.layer || 0));
    const elRef = doc(firestore, `moodboards/${boardId}/elements`, selectedId);
    await updateDoc(elRef, { layer: (el.layer || 0) + 1 > maxLayer ? maxLayer : (el.layer || 0) + 1 });
  };
  const sendBackward = async () => {
    if (!selectedId) return;
    const el = elements.find(e => e.id === selectedId);
    if (!el) return;
    const elRef = doc(firestore, `moodboards/${boardId}/elements`, selectedId);
    await updateDoc(elRef, { layer: Math.max(0, (el.layer || 0) - 1) });
  };
  const toggleLock = async () => {
    if (!selectedId) return;
    const el = elements.find(e => e.id === selectedId);
    if (!el) return;
    const elRef = doc(firestore, `moodboards/${boardId}/elements`, selectedId);
    await updateDoc(elRef, { locked: !el.locked });
  };

  // Delete element
  const handleDelete = async () => {
    if (!selectedId) return;
    const elRef = doc(firestore, `moodboards/${boardId}/elements`, selectedId);
    await deleteDoc(elRef);
    setSelectedId(null);
  };

  // Render
  const getElProps = (el: BoardElement) => {
    if (resizingId === el.id && localTransforms[el.id]) {
      return localTransforms[el.id];
    }
    return {
      width: el.width,
      height: el.height,
      rotation: el.rotation || 0,
      x: el.x,
      y: el.y,
    };
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Toolbar */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<TextFieldsIcon />} onClick={() => setShowTextDialog(true)}>
          Add Text
        </Button>
        <Button variant="contained" startIcon={<AddPhotoAlternateIcon />} component="label">
          Add Image
          <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleImageUpload} />
        </Button>
        <Button variant="contained" startIcon={<PaletteIcon />} onClick={() => setShowColorPicker(v => !v)}>
          Color Picker
        </Button>
        <Button variant="outlined" startIcon={<CropSquareIcon />} onClick={handleAddRect}>Rectangle</Button>
        <Button variant="outlined" startIcon={<RadioButtonUncheckedIcon />} onClick={handleAddCircle}>Circle</Button>
        <Button variant="outlined" startIcon={<EmojiEmotionsIcon />} onClick={handleAddSticker}>Sticker</Button>
        <Button variant="outlined" startIcon={<LayersIcon />} onClick={bringForward} disabled={!selectedId}>Bring Forward</Button>
        <Button variant="outlined" startIcon={<LayersIcon />} onClick={sendBackward} disabled={!selectedId}>Send Backward</Button>
        <Button variant="outlined" startIcon={elements.find(e => e.id === selectedId)?.locked ? <LockOpenIcon /> : <LockIcon />} onClick={toggleLock} disabled={!selectedId}>
          {elements.find(e => e.id === selectedId)?.locked ? 'Unlock' : 'Lock'}
        </Button>
      </Stack>
      {/* Canvas */}
      <Stage
        ref={stageRef}
        width={BOARD_WIDTH}
        height={BOARD_HEIGHT}
        style={{ background: '#f8fafc', borderRadius: 12, boxShadow: '0 2px 8px #0001', cursor: 'grab' }}
        onMouseDown={e => {
          if (e.target === e.target.getStage()) handleDeselect();
        }}
      >
        <Layer>
          {[...elements].sort((a, b) => (a.layer || 0) - (b.layer || 0)).map(el => {
            if (el.type === 'image') {
              const img = new window.Image();
              img.src = el.content;
              return (
                <KonvaImage
                  key={el.id}
                  id={`el-${el.id}`}
                  image={img}
                  {...getElProps(el)}
                  draggable={!el.locked}
                  onClick={() => handleSelect(el.id)}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onTransformStart={() => { setResizingId(el.id); }}
                  onTransform={(e) => {
                    const node = e.target;
                    setLocalTransforms(prev => ({
                      ...prev,
                      [el.id]: {
                        width: node.width() * node.scaleX(),
                        height: node.height() * node.scaleY(),
                        rotation: node.rotation(),
                        x: node.x(),
                        y: node.y(),
                      }
                    }));
                  }}
                  onTransformEnd={async (e) => {
                    const node = e.target;
                    setResizingId(null);
                    setLocalTransforms(prev => { const copy = { ...prev }; delete copy[el.id]; return copy; });
                    await handleTransform(el.id, node);
                  }}
                />
              );
            }
            if (el.type === 'text') {
              return (
                <KonvaText
                  key={el.id}
                  id={`el-${el.id}`}
                  text={el.content}
                  {...getElProps(el)}
                  fontSize={el.fontSize || 28}
                  fill={el.color || '#222'}
                  draggable={!el.locked}
                  onClick={() => handleSelect(el.id)}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onTransformEnd={e => handleTransform(el.id, e.target)}
                />
              );
            }
            if (el.type === 'rect') {
              return (
                <Rect
                  key={el.id}
                  id={`el-${el.id}`}
                  {...getElProps(el)}
                  fill={el.color || '#FFD93D'}
                  draggable={!el.locked}
                  onClick={() => handleSelect(el.id)}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onTransformEnd={e => handleTransform(el.id, e.target)}
                />
              );
            }
            if (el.type === 'circle') {
              return (
                <Shape
                  key={el.id}
                  id={`el-${el.id}`}
                  sceneFunc={(context: Konva.Context, shape: Konva.Shape) => {
                    context.beginPath();
                    context.arc(el.x + el.width / 2, el.y + el.height / 2, el.width / 2, 0, Math.PI * 2, false);
                    context.closePath();
                    context.fillStrokeShape(shape);
                  }}
                  {...getElProps(el)}
                  fill={el.color || '#FFD93D'}
                  draggable={!el.locked}
                  onClick={() => handleSelect(el.id)}
                  onDragEnd={(e: any) => handleDrag(el.id, e.target.x(), e.target.y())}
                  onTransformEnd={(e: any) => handleTransform(el.id, e.target)}
                />
              );
            }
            if (el.type === 'sticker') {
              return (
                <KonvaText
                  key={el.id}
                  id={`el-${el.id}`}
                  text={el.content}
                  {...getElProps(el)}
                  fontSize={48}
                  draggable={!el.locked}
                  onClick={() => handleSelect(el.id)}
                  onDragEnd={e => handleDrag(el.id, e.target.x(), e.target.y())}
                  onTransformEnd={e => handleTransform(el.id, e.target)}
                />
              );
            }
            return null;
          })}
          {/* Transformer for selected */}
          {selectedId && (
            <Transformer
              ref={transformerRef}
              nodes={(() => {
                const stage = stageRef.current?.getStage();
                if (!stage) return [];
                const node = stage.findOne(`#el-${selectedId}`);
                return node ? [node] : [];
              })()}
              rotateEnabled
              enabledAnchors={["top-left","top-right","bottom-left","bottom-right"]}
            />
          )}
        </Layer>
      </Stage>
      {/* Add Text Dialog */}
      <Dialog open={showTextDialog} onClose={() => setShowTextDialog(false)}>
        <DialogTitle>Add Text</DialogTitle>
        <DialogContent>
          <TextField
            value={newText}
            onChange={e => setNewText(e.target.value)}
            fullWidth
            autoFocus
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTextDialog(false)}>Cancel</Button>
          <Button onClick={handleAddText} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
      {/* Color Picker (placeholder) */}
      {showColorPicker && (
        <Box sx={{ position: 'absolute', zIndex: 1000, top: 80, left: 40, bgcolor: 'white', p: 2, borderRadius: 2, boxShadow: 3 }}>
          <div>Color picker coming soon...</div>
          <Button sx={{ mt: 1 }} onClick={() => setShowColorPicker(false)}>Close</Button>
        </Box>
      )}
      {selectedId && (
        <Box sx={{ position: 'absolute', top: 16, right: 32, zIndex: 1001 }}>
          <Tooltip title="Delete">
            <IconButton color="error" onClick={handleDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default MoodBoardEditorV2; 